/**
 * Anonymous (auto-provisioned) storefront session marker.
 *
 * WHY THIS EXISTS -- the thing no other signal can tell you.
 *
 * The storefront does not leave an unauthenticated visitor logged out. Both
 * anonymous entry paths swap the visitor into the STORE'S SHARED shopper
 * account:
 *
 *   1. StorefrontSessionGate.loginShopper() -- every visit to /shop/<slug>
 *      that is not already a shopper session and is not privileged/preview.
 *   2. TheLoginPage.loginShopperFromURL() -- the /login/<storeUrl> entry.
 *
 * Both resolve the account through `/campaign/getShopperFromCampaignID`,
 * whose SQL selects the FIRST `role_id = 5` account joined to the store
 * (api/src/endpoints/campaign.py:1901). That is ONE row per store, shared by
 * every visitor. The swap then installs a real token, a real `userId`, and
 * `roleName = "Shopper"`.
 *
 * The consequence is that after the swap an anonymous visitor is
 * INDISTINGUISHABLE from a person who signed in, by every signal that
 * existed before this module:
 *
 *   - `isLoggedIn`  -> true for both (state.account is populated)
 *   - `isShopper`   -> true for both (role.name === 'Shopper')
 *   - `roleName`    -> "Shopper" for both
 *   - token/userId  -> present and real for both
 *
 * So the distinction cannot be DERIVED; it has to be RECORDED, at the one
 * moment it is known -- the moment the swap happens. That is what this
 * module does.
 *
 * WHY localStorage rather than in-memory or Vuex: the marker has to remain
 * true across a reload for exactly as long as the token it describes does. A
 * marker in module state or Vuex would evaporate on refresh while the token
 * survived, and the shared session would then be treated as personal -- which
 * is the bug, reintroduced on every F5.
 *
 * WHY NOT compare the account id to the store's shopper id: that answer is
 * only available via `/campaign/getShopperFromCampaignID`, i.e. a network
 * round-trip, and the layout needs the answer synchronously on every render.
 *
 * WHAT IT GATES: whether the storefront chrome offers Profile / Orders /
 * Addresses / Logout. Those link to PERSONAL data held on a SHARED account,
 * so offering them to an anonymous visitor is what exposes one visitor's
 * orders and saved addresses to the next.
 *
 * FAIL-SAFE DIRECTION: an unreadable storage reports ANONYMOUS (true). Under
 * uncertainty about whose session this is, the safe answer to "should I show
 * this person's saved addresses?" is no. Reporting "real session" on an error
 * would unlock precisely the links this marker exists to withhold.
 *
 * WHAT THIS FLAG DOES *NOT* MEAN -- important for the password-gate work that
 * follows this unit.
 *
 * It does NOT mean "this visitor has never passed a store password gate". It
 * means exactly one thing: "this session is riding the store's SHARED
 * auto-provisioned account rather than a personal one". Those are orthogonal,
 * and the code already treats them that way: the password path
 * (StorefrontSessionGate.onSignedIn) funnels into the very same
 * loginShopper(), so a visitor who clears a password-protected store's gate is
 * marked anonymous TOO -- because passing that gate lands them on the shared
 * account, exactly like the open-store path. `validate_store_password` takes
 * only {password, campaign_id} and no user identifier, so it cannot establish
 * an identity even in principle.
 *
 * That is the behaviour the planned change wants ("passing the password should
 * leave you ANONYMOUS rather than logged into the shared account"), so a
 * visitor who is BOTH password-cleared AND anonymous is representable today
 * with no change to this module. If the password-cleared state later needs to
 * be REMEMBERED (e.g. to stop re-prompting on every page), that is a SEPARATE
 * flag about a different question -- do not overload this one, or the two
 * meanings will drift and the personal links will key off the wrong one.
 */

export const ANONYMOUS_SESSION_KEY = "storefrontAnonymousSession";

/**
 * Record that the CURRENT session is the store's shared, auto-provisioned
 * shopper -- not a person who signed in. Call this at the point of the token
 * swap, alongside the token write.
 */
export function markAnonymousStorefrontSession() {
    try {
        localStorage.setItem(ANONYMOUS_SESSION_KEY, "1");
    } catch {
        // Storage unavailable. isAnonymousStorefrontSession() fails toward
        // "anonymous" in that case, so the personal links stay hidden and no
        // data is exposed by the write having been lost.
    }
}

/**
 * Drop the marker. Call when the session is torn down (logout) or replaced
 * by a genuine personal sign-in -- the two events that make the session no
 * longer the shared auto-provisioned one.
 */
export function clearAnonymousStorefrontSession() {
    try {
        localStorage.removeItem(ANONYMOUS_SESSION_KEY);
    } catch {
        // See markAnonymousStorefrontSession.
    }
}

/**
 * True when the current session is the store's shared auto-provisioned
 * shopper (or when we cannot tell -- see FAIL-SAFE DIRECTION above).
 */
export function isAnonymousStorefrontSession() {
    try {
        return localStorage.getItem(ANONYMOUS_SESSION_KEY) === "1";
    } catch {
        // Cannot read => cannot prove this is a personal session => withhold
        // the personal links.
        return true;
    }
}

export default isAnonymousStorefrontSession;
